import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, Check, X, Loader2, CheckCircle, ArrowRight, ChevronDown, Lock } from 'lucide-react';
import ReactCountryFlag from 'react-country-flag';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WL_INTENT_KEY = 'cloudsetu_wl_order_intent';

const COUNTRY_CODES = [
  { code: '+91', iso: 'IN', country: 'India' },
  { code: '+1', iso: 'US', country: 'United States' },
  { code: '+44', iso: 'GB', country: 'United Kingdom' },
  { code: '+971', iso: 'AE', country: 'UAE' },
  { code: '+65', iso: 'SG', country: 'Singapore' },
  { code: '+61', iso: 'AU', country: 'Australia' },
  { code: '+49', iso: 'DE', country: 'Germany' },
  { code: '+33', iso: 'FR', country: 'France' },
  { code: '+81', iso: 'JP', country: 'Japan' },
  { code: '+86', iso: 'CN', country: 'China' },
  { code: '+7', iso: 'RU', country: 'Russia' },
  { code: '+55', iso: 'BR', country: 'Brazil' },
  { code: '+27', iso: 'ZA', country: 'South Africa' },
  { code: '+92', iso: 'PK', country: 'Pakistan' },
  { code: '+880', iso: 'BD', country: 'Bangladesh' },
];

const ACCOUNT_TYPES = [
  'Govt Company', 'LLP', 'NBFC', 'Private Limited',
  'Proprietorship / Individual', 'Public Limited', 'Trust / NGO / Society / HUF',
];

const INDIAN_STATES = [
  'Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh','Goa',
  'Gujarat','Haryana','Himachal Pradesh','Jharkhand','Karnataka','Kerala',
  'Madhya Pradesh','Maharashtra','Manipur','Meghalaya','Mizoram','Nagaland',
  'Odisha','Punjab','Rajasthan','Sikkim','Tamil Nadu','Telangana','Tripura',
  'Uttar Pradesh','Uttarakhand','West Bengal',
  'Andaman and Nicobar Islands','Chandigarh','Dadra & Nagar Haveli and Daman & Diu',
  'Delhi','Jammu and Kashmir','Ladakh','Lakshadweep','Puducherry',
];

const DEFAULT_CLIENT_BULLETS = [
  'Instant account activation',
  'Access to all cloud services',
  'Dedicated support 24/7',
  'Secure & DPDP-compliant platform',
  'No setup or hidden charges',
];

import { PasswordStrengthChecklist, PASSWORD_REQUIREMENTS } from '@/components/PasswordStrengthChecklist';
import { handleGSTInput as gstPositionalInput, validateGST } from '@/utils/inputValidation';
import { useBranding } from '@/context/BrandingContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const handleGSTInput = gstPositionalInput;
const handleMobileInput = (val) => val.replace(/\D/g, '').slice(0, 10);

/* Country code picker (compact) — unchanged */
const CountryCodePicker = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);
  const selected = COUNTRY_CODES.find(c => c.code === value) || COUNTRY_CODES[0];

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = search
    ? COUNTRY_CODES.filter(c => c.country.toLowerCase().includes(search.toLowerCase()) || c.code.includes(search))
    : COUNTRY_CODES;

  return (
    <div ref={ref} className="relative">
      <button type="button" onClick={() => setOpen(o => !o)}
        className="h-12 flex items-center gap-1.5 px-2.5 bg-slate-50 border border-slate-200 rounded-lg hover:bg-slate-100 min-w-[68px]">
        <ReactCountryFlag countryCode={selected.iso} svg style={{ width: '1.4em', height: '1.4em' }} />
        <ChevronDown className={`h-3 w-3 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-50 top-full left-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-xl w-56 flex flex-col" style={{ maxHeight: '260px' }}>
          <div className="p-2 border-b">
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search country..."
              className="w-full px-2 py-1.5 text-sm bg-slate-50 border border-slate-200 rounded-lg outline-none" autoFocus />
          </div>
          <div className="overflow-y-auto flex-1">
            {filtered.map((c, i) => (
              <button key={`${c.iso}-${i}`} type="button" onClick={() => { onChange(c.code); setOpen(false); setSearch(''); }}
                className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 text-left ${c.code === value ? 'bg-blue-50 text-blue-700 font-medium' : 'text-slate-700'}`}>
                <ReactCountryFlag countryCode={c.iso} svg style={{ width: '1.2em', height: '1.2em' }} />
                <span className="flex-1 truncate text-xs">{c.country}</span>
                <span className="text-slate-400 text-xs font-mono">{c.code}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default function ClientSignup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const { branding, isCustomDomain } = useBranding();

  const urlRef = searchParams.get('ref') ? searchParams.get('ref').toUpperCase() : null;

  useEffect(() => {
    try { localStorage.removeItem('cloudsetu_pending_ref'); } catch {}
  }, []);

  const [signupOffer, setSignupOffer] = useState(null);
  const [offerLoaded, setOfferLoaded] = useState(false);
  useEffect(() => {
    // SP/CP k clients ke liye offer nahi dikhana — urlRef present ho to skip
    if (urlRef) { setOfferLoaded(true); return; }
    axios.get(`${API}/active-direct-client-signup-offer`)
      .then(res => { if (res.data?.id) setSignupOffer(res.data); })
      .catch(() => {})
      .finally(() => setOfferLoaded(true));
  }, [urlRef]);

  const [form, setForm] = useState({
    full_name: '', email: '', mobile: '', mobile_country_code: '+91',
    password: '', confirm_password: '', business_name: '', account_type: '',
    address: '', city: '', state_name: '', pincode: '', gst_number: '', terms_accepted: false,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [emailCheck, setEmailCheck] = useState({ status: 'idle' });
  const emailDebounceRef = useRef(null);
  const [refStatus, setRefStatus] = useState({ status: 'idle', data: null });

  useEffect(() => {
    if (!urlRef) return;
    setRefStatus({ status: 'checking', data: null });
    axios.get(`${API}/validate-referral?code=${encodeURIComponent(urlRef)}`)
      .then(res => setRefStatus({ status: 'valid', data: res.data }))
      .catch(() => setRefStatus({ status: 'invalid', data: null }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'mobile') { setForm(f => ({ ...f, mobile: handleMobileInput(value) })); return; }
    if (name === 'gst_number') { setForm(f => ({ ...f, gst_number: handleGSTInput(value) })); return; }
    if (name === 'pincode') { setForm(f => ({ ...f, pincode: value.replace(/\D/g, '').slice(0, 6) })); return; }
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const validate = () => {
    if (!form.full_name.trim()) { toast.error('Full name is required'); return false; }
    if (!form.email.trim()) { toast.error('Email is required'); return false; }
    if (emailCheck.status === 'taken') { toast.error('Email already registered'); return false; }
    if (emailCheck.status === 'checking') { toast.error('Please wait while we verify your email'); return false; }
    if (!form.mobile || form.mobile.length !== 10) { toast.error('Mobile number must be 10 digits'); return false; }
    if (!form.password) { toast.error('Password is required'); return false; }
    if (!PASSWORD_REQUIREMENTS.every(r => r.test(form.password))) { toast.error('Password does not meet security requirements'); return false; }
    if (form.password !== form.confirm_password) { toast.error('Passwords do not match'); return false; }
    if (!form.business_name.trim()) { toast.error('Business name is required'); return false; }
    if (!form.account_type) { toast.error('Account type is required'); return false; }
    if (!form.city.trim()) { toast.error('City is required'); return false; }
    if (!form.state_name) { toast.error('State is required'); return false; }
    if (!form.pincode || form.pincode.length !== 6) { toast.error('Pincode must be 6 digits'); return false; }
    if (form.gst_number && !/^\d{2}[A-Z]{5}\d{4}[A-Z][A-Z0-9]Z[A-Z0-9]$/.test(form.gst_number)) { toast.error('Invalid GST number format'); return false; }
    if (!form.terms_accepted) { toast.error('Please accept the Terms & Conditions'); return false; }
    if (urlRef && refStatus.status === 'invalid') { toast.error('Invalid partner code in referral link'); return false; }
    if (urlRef && refStatus.status === 'checking') { toast.error('Please wait while we verify the referral code'); return false; }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const payload = {
        full_name: form.full_name.trim(), email: form.email.trim().toLowerCase(),
        mobile: form.mobile, mobile_country_code: form.mobile_country_code,
        password: form.password, business_name: form.business_name.trim(),
        account_type: form.account_type, address: form.address.trim(),
        city: form.city.trim(), state_name: form.state_name,
        pincode: form.pincode, gst_number: form.gst_number, terms_accepted: true,
      };
      if (urlRef) payload.referral_code = urlRef;
      const res = await axios.post(`${API}/client/signup`, payload);

      const rawIntent = sessionStorage.getItem(WL_INTENT_KEY);
      if (rawIntent && res.data.path === 'B') {
        try {
          const intent = JSON.parse(rawIntent);
          await login(form.email.trim().toLowerCase(), form.password);
          navigate(intent.whiteLabelUrl || `/public/${intent.subdomain}`);
          return;
        } catch {
          try {
            const intent = JSON.parse(rawIntent);
            navigate(`/login?next=${encodeURIComponent(intent.whiteLabelUrl || `/public/${intent.subdomain}`)}`);
          } catch { setSuccess(res.data); }
          return;
        }
      }
      setSuccess(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Signup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Success screen (unchanged) ──
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4"
        style={{ background: 'linear-gradient(145deg, #f0f4f8 0%, #d9e2ec 50%, #e4ecf4 100%)' }}>
        <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="flex justify-center mb-6">
            {isCustomDomain && branding?.logo_url ? (
              <img
                src={branding.logo_url.startsWith('/') ? `${BACKEND_URL}${branding.logo_url}` : branding.logo_url}
                alt={branding.company_name || 'Logo'} className="h-12 object-contain"
              />
            ) : (
              <img src="/cloudsetu-logo.png" alt="CloudSetu" className="h-12 object-contain" />
            )}
          </div>
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          <h2 className="text-xl font-semibold text-slate-800 mb-2">Account Created!</h2>
          <p className="text-slate-500 text-sm mb-4">{success.message}</p>
          {success.path === 'B' ? (
            <Button onClick={() => navigate('/login')} className="w-full h-12"
              style={{ background: 'linear-gradient(90deg, #2563eb 0%, #06b6d4 100%)', border: 'none' }}>
              <ArrowRight className="mr-2 h-4 w-4" /> Log In to your Account
            </Button>
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
              You will receive an email notification once your partner approves your account.
            </div>
          )}
        </div>
      </div>
    );
  }

  // Offer bullets for active offer card
  const clientOfferBullets = signupOffer ? [
    signupOffer.wallet_credit_amount > 0 && `₹${Number(signupOffer.wallet_credit_amount).toLocaleString('en-IN')} wallet credit on registration`,
    'No setup or hidden charges',
    'Instant account activation',
    '24/7 dedicated support',
  ].filter(Boolean) : [];

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-8"
      style={{ background: '#fff' }}
    >

      <div
        className="w-full flex rounded-2xl overflow-hidden shadow-[0_8px_48px_rgba(15,30,61,0.18),0_0_0_1px_rgba(255,255,255,0.9)] client-signup-wrap"
        style={{ position: 'relative', zIndex: 1, maxWidth: 960 }}
        data-testid="client-signup-card"
      >
        {/* ══ LEFT PANEL: always show branding + offer below if active ══ */}
        <div
          className="flex flex-col p-8 client-offer-panel overflow-y-auto"
          style={{
            background: signupOffer?.bg_color || 'linear-gradient(145deg, #1a3a8f 0%, #0f2260 100%)',
            color: signupOffer?.text_color || '#fff',
            flex: '0 0 38%',
            minWidth: 0,
          }}
          data-testid="client-offer-panel"
        >
          {/* ── DEFAULT / PARTNER BRANDING (always visible) ── */}
          {isCustomDomain && branding?.logo_url ? (
            <img
              src={branding.logo_url.startsWith('/') ? `${BACKEND_URL}${branding.logo_url}` : branding.logo_url}
              alt={branding.company_name || 'Logo'}
              className="h-10 object-contain self-start mb-5"
            />
          ) : (
            <span
              className="inline-block self-start px-3 py-1 rounded-full text-xs font-bold mb-5"
              style={{ background: '#FF6B35', color: '#fff' }}
            >
              ✦ CloudSetu
            </span>
          )}

          <p className="font-extrabold leading-snug mb-2" style={{ fontSize: 'clamp(20px, 2.5vw, 26px)' }}>
            {isCustomDomain && branding?.company_name ? `Welcome to ${branding.company_name}` : 'Welcome to CloudSetu'}
          </p>
          <p className="text-sm mb-5" style={{ opacity: 0.75 }}>
            Create your account and get access to all cloud services instantly.
          </p>

          <ul className="space-y-2 mb-6">
            {DEFAULT_CLIENT_BULLETS.map((b, i) => (
              <li key={i} className="flex items-start gap-2 text-sm" style={{ opacity: 0.9 }}>
                <span
                  className="mt-0.5 flex-shrink-0 rounded-full flex items-center justify-center"
                  style={{ width: 18, height: 18, background: 'rgba(255,255,255,0.2)' }}
                >
                  <Check size={11} strokeWidth={3} />
                </span>
                {b}
              </li>
            ))}
          </ul>

          {/* Referred by badge — if ref code in URL */}
          {urlRef && refStatus.status === 'valid' && refStatus.data && (
            <div
              className="rounded-xl px-4 py-3 mb-4"
              style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)' }}
            >
              <p className="text-xs font-semibold mb-0.5" style={{ opacity: 0.75 }}>🤝 Referred by</p>
              <p className="text-sm font-bold">{refStatus.data.partner_name}</p>
            </div>
          )}

          {/* ── ACTIVE OFFER DETAILS (only for admin-direct clients, not SP/CP clients) ── */}
          {offerLoaded && signupOffer && !urlRef && (
            <div
              className="rounded-xl p-4 mt-2"
              style={{ background: 'rgba(255,255,255,0.10)', border: '1px solid rgba(255,255,255,0.18)' }}
            >
              {/* Offer badge */}
              {signupOffer.badge_text && (
                <span
                  className="inline-block px-2 py-0.5 rounded-full text-xs font-bold mb-3"
                  style={{ background: signupOffer.badge_color || '#FF6B35', color: '#fff' }}
                >
                  {signupOffer.badge_text}
                </span>
              )}
              {!signupOffer.badge_text && (
                <span
                  className="inline-block px-2 py-0.5 rounded-full text-xs font-bold mb-3"
                  style={{ background: signupOffer.badge_color || '#FF6B35', color: '#fff' }}
                >
                  {signupOffer.name || 'Special Offer'}
                </span>
              )}

              {/* Wallet credit */}
              {signupOffer.wallet_credit_amount > 0 && (
                <div className="mb-3">
                  <p className="text-xs font-medium mb-0.5" style={{ opacity: 0.75 }}>🎁 You'll receive</p>
                  <p
                    className="font-extrabold leading-none"
                    style={{ fontSize: 'clamp(28px, 4vw, 38px)', color: signupOffer.badge_color || '#FF6B35' }}
                  >
                    ₹{Number(signupOffer.wallet_credit_amount).toLocaleString('en-IN')}
                  </p>
                  <p className="text-xs font-medium mt-0.5" style={{ opacity: 0.75 }}>in Free Wallet Credits</p>
                </div>
              )}

              {/* Offer title if no wallet amount */}
              {!signupOffer.wallet_credit_amount && signupOffer.title && (
                <p className="font-bold mb-2" style={{ fontSize: 'clamp(14px, 2vw, 18px)' }}>
                  {signupOffer.title}
                </p>
              )}

              {/* Offer bullets */}
              {clientOfferBullets.length > 0 && (
                <ul className="space-y-1.5 mb-3">
                  {clientOfferBullets.map((b, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs" style={{ opacity: 0.9 }}>
                      <span
                        className="mt-0.5 flex-shrink-0 rounded-full flex items-center justify-center"
                        style={{ width: 15, height: 15, background: 'rgba(255,255,255,0.2)' }}
                      >
                        <Check size={9} strokeWidth={3} />
                      </span>
                      {b}
                    </li>
                  ))}
                </ul>
              )}

              {/* Valid till / description */}
              {signupOffer.end_date && (
                <p className="text-xs" style={{ opacity: 0.55 }}>
                  Valid till {signupOffer.end_date.split('T')[0]}
                </p>
              )}
              {!signupOffer.end_date && (
                <p className="text-xs" style={{ opacity: 0.45 }}>
                  {signupOffer.description || 'Limited time offer. T&C apply.'}
                </p>
              )}
            </div>
          )}

          {/* Footer */}
          <p className="text-xs mt-auto pt-6" style={{ opacity: 0.4 }}>
            Trusted by businesses across India. No hidden charges.
          </p>
        </div>

        {/* ══ RIGHT: Form Panel (unchanged) ══ */}
        <div className="flex-1 bg-white p-8 min-w-0 overflow-y-auto" style={{ maxHeight: '100vh' }}>
          {/* Logo */}
          <div className="flex justify-center mb-6">
            {isCustomDomain && branding?.logo_url ? (
              <img
                src={branding.logo_url.startsWith('/') ? `${BACKEND_URL}${branding.logo_url}` : branding.logo_url}
                alt={branding.company_name || 'Logo'} className="h-12 object-contain"
              />
            ) : (
              <img src="/cloudsetu-logo.png" alt="CloudSetu" className="h-12 object-contain" />
            )}
          </div>

          {/* Header */}
          <div className="text-center mb-6">
            <h1 className="text-2xl font-semibold text-slate-800 mb-2" data-testid="client-signup-title">
              Create your Account
            </h1>
            <p className="text-slate-500 text-sm">Fill in your details to get started</p>
            {refStatus.status === 'valid' && refStatus.data && (
              <div className="mt-3 bg-green-50 border border-green-200 rounded-xl px-4 py-2 text-sm text-green-700 inline-flex items-center gap-2">
                <Check className="h-4 w-4" /> Referred by <strong>{refStatus.data.partner_name}</strong>
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Personal Info */}
            <div className="space-y-1">
              <Label className="text-slate-700 text-sm font-medium">Full Name <span className="text-red-500">*</span></Label>
              <Input name="full_name" placeholder="John Doe" value={form.full_name} onChange={handleChange} required
                className="h-11 bg-slate-50 border-slate-200" data-testid="signup-full-name" />
            </div>

            {/* Email */}
            <div className="space-y-1">
              <Label className="text-slate-700 text-sm font-medium">Email <span className="text-red-500">*</span></Label>
              <div className="relative">
                <Input name="email" type="email" placeholder="you@company.com" value={form.email} onChange={handleChange} required
                  className={`h-11 bg-slate-50 border-slate-200 pr-10 ${emailCheck.status === 'taken' ? 'border-red-400' : emailCheck.status === 'available' ? 'border-green-400' : ''}`}
                  data-testid="signup-email" />
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {emailCheck.status === 'checking' && <Loader2 className="h-4 w-4 text-slate-400 animate-spin" />}
                  {emailCheck.status === 'available' && <Check className="h-4 w-4 text-green-500" />}
                  {emailCheck.status === 'taken' && <X className="h-4 w-4 text-red-500" />}
                </div>
              </div>
              {emailCheck.status === 'taken' && <p className="text-xs text-red-500">Email already registered</p>}
            </div>

            {/* Mobile */}
            <div className="space-y-1">
              <Label className="text-slate-700 text-sm font-medium">Mobile Number <span className="text-red-500">*</span></Label>
              <div className="flex gap-2">
                <CountryCodePicker value={form.mobile_country_code} onChange={val => setForm(f => ({ ...f, mobile_country_code: val }))} />
                <Input name="mobile" placeholder="10-digit number" value={form.mobile} onChange={handleChange} maxLength={10} required
                  className="h-11 bg-slate-50 border-slate-200 flex-1" data-testid="signup-mobile" />
              </div>
            </div>

            {/* Password */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-slate-700 text-sm font-medium">Password <span className="text-red-500">*</span></Label>
                <div className="relative">
                  <Input name="password" type={showPassword ? 'text' : 'password'} placeholder="••••••••"
                    value={form.password} onChange={handleChange} required
                    className="h-11 bg-slate-50 border-slate-200 pr-10" data-testid="signup-password" />
                  <button type="button" onClick={() => setShowPassword(p => !p)} tabIndex={-1}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-slate-700 text-sm font-medium">Confirm Password <span className="text-red-500">*</span></Label>
                <div className="relative">
                  <Input name="confirm_password" type={showConfirmPassword ? 'text' : 'password'} placeholder="••••••••"
                    value={form.confirm_password} onChange={handleChange} required
                    className={`h-11 bg-slate-50 border-slate-200 pr-10 ${form.confirm_password && form.password !== form.confirm_password ? 'border-red-300' : ''}`}
                    data-testid="signup-confirm-password" />
                  <button type="button" onClick={() => setShowConfirmPassword(p => !p)} tabIndex={-1}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            </div>

            {/* Password requirements */}
            {form.password && (
              <div className="grid grid-cols-2 gap-1 p-3 bg-slate-50 rounded-lg">
                {PASSWORD_REQUIREMENTS.map((req, i) => {
                  const ok = req.test(form.password);
                  return (
                    <div key={i} className={`flex items-center gap-1.5 text-xs ${ok ? 'text-green-600' : 'text-slate-400'}`}>
                      {ok ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                      {req.label}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Business Info */}
            <div className="border-t border-slate-100 pt-4">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">Business Information</p>
              <div className="space-y-3">
                <div className="space-y-1">
                  <Label className="text-slate-700 text-sm font-medium">Business / Account Name <span className="text-red-500">*</span></Label>
                  <Input name="business_name" placeholder="Acme Pvt Ltd" value={form.business_name} onChange={handleChange} required
                    className="h-11 bg-slate-50 border-slate-200" data-testid="signup-business-name" />
                </div>
                <div className="space-y-1">
                  <Label className="text-slate-700 text-sm font-medium">Account Type <span className="text-red-500">*</span></Label>
                  <select name="account_type" value={form.account_type} onChange={handleChange} required
                    className="w-full h-11 bg-slate-50 border border-slate-200 rounded-md px-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
                    data-testid="signup-account-type">
                    <option value="">Select account type</option>
                    {ACCOUNT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="space-y-1">
                  <Label className="text-slate-700 text-sm font-medium">GST Number</Label>
                  <Input name="gst_number" placeholder="22AAAAA0000A1Z5" value={form.gst_number} onChange={handleChange}
                    maxLength={15} className="h-11 bg-slate-50 border-slate-200 font-mono text-sm"
                    data-testid="signup-gst" />
                  {form.gst_number.length > 0 && form.gst_number.length < 15 && (
                    <p className="text-xs text-amber-600">{15 - form.gst_number.length} characters remaining</p>
                  )}
                  {form.gst_number.length === 15 && validateGST(form.gst_number) && (
                    <p className="text-xs text-red-500" data-testid="gst-error">{validateGST(form.gst_number)}</p>
                  )}
                  {form.gst_number.length === 15 && !validateGST(form.gst_number) && (
                    <p className="text-xs text-green-600" data-testid="gst-valid">Valid GSTIN format</p>
                  )}
                </div>
              </div>
            </div>

            {/* Address */}
            <div className="border-t border-slate-100 pt-4">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">Address</p>
              <div className="space-y-3">
                <div className="space-y-1">
                  <Label className="text-slate-700 text-sm font-medium">Address Line</Label>
                  <Input name="address" placeholder="Street, Area" value={form.address} onChange={handleChange}
                    className="h-11 bg-slate-50 border-slate-200" data-testid="signup-address" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-slate-700 text-sm font-medium">City <span className="text-red-500">*</span></Label>
                    <Input name="city" placeholder="Mumbai" value={form.city} onChange={handleChange} required
                      className="h-11 bg-slate-50 border-slate-200" data-testid="signup-city" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-slate-700 text-sm font-medium">Pincode <span className="text-red-500">*</span></Label>
                    <Input name="pincode" placeholder="400001" value={form.pincode} onChange={handleChange}
                      maxLength={6} required className="h-11 bg-slate-50 border-slate-200 font-mono"
                      data-testid="signup-pincode" />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-slate-700 text-sm font-medium">State <span className="text-red-500">*</span></Label>
                  <select name="state_name" value={form.state_name} onChange={handleChange} required
                    className="w-full h-11 bg-slate-50 border border-slate-200 rounded-md px-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
                    data-testid="signup-state">
                    <option value="">Select state</option>
                    {INDIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
            </div>

            {/* Partner Code — only when ?ref= in URL */}
            {urlRef && (
              <div className="border-t border-slate-100 pt-4">
                <div className="space-y-1">
                  <Label className="text-slate-700 text-sm font-medium">Partner Code</Label>
                  <div className="relative">
                    <Input name="referral_code" placeholder="SP-25-0001" value={urlRef} readOnly disabled
                      className="h-11 bg-amber-50 border-amber-200 font-mono text-sm pr-10 opacity-75 cursor-not-allowed"
                      data-testid="signup-referral-code" />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {refStatus.status === 'checking' && <Loader2 className="h-4 w-4 text-slate-400 animate-spin" />}
                      {refStatus.status === 'valid' && <Lock className="h-4 w-4 text-amber-400" />}
                      {refStatus.status === 'invalid' && <X className="h-4 w-4 text-red-500" />}
                    </div>
                  </div>
                  {refStatus.status === 'valid' && refStatus.data && (
                    <p className="text-xs text-green-600">Referred by: <strong>{refStatus.data.partner_name}</strong></p>
                  )}
                  {refStatus.status === 'invalid' && (
                    <p className="text-xs text-red-500">This referral link is invalid or expired. You may still register — you will be placed under Admin.</p>
                  )}
                </div>
              </div>
            )}

            {/* T&C */}
            <div className="flex items-start gap-3 pt-2">
              <input type="checkbox" id="terms" name="terms_accepted" checked={form.terms_accepted}
                onChange={handleChange} className="mt-0.5 h-4 w-4 rounded border-slate-300 accent-blue-600"
                data-testid="signup-terms" />
              <label htmlFor="terms" className="text-sm text-slate-600">
                I agree to the{' '}
                <a href="https://cloudsetu.com/legal/terms-of-service" className="text-blue-600 hover:underline">Terms & Conditions</a>
                {' '}and{' '}
                <a href="https://cloudsetu.com/legal/privacy-policy" className="text-blue-600 hover:underline">Privacy Policy</a>
              </label>
            </div>

            <Button type="submit" disabled={loading} className="w-full h-12 text-white font-medium rounded-lg mt-2"
              style={{ background: 'linear-gradient(90deg, #2563eb 0%, #06b6d4 100%)', border: 'none' }}
              data-testid="client-signup-submit">
              {loading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Creating Account...</> : 'Create Account'}
            </Button>
          </form>

          <p className="text-sm text-center text-slate-500 mt-6">
            Already have an account?{' '}
            <a href="/login" className="text-cyan-600 hover:text-cyan-700 font-medium" data-testid="client-login-link">
              Sign In
            </a>
          </p>
        </div>{/* end form panel */}
      </div>

      {/* Mobile: stack vertically */}
      <style>{`
        @media (max-width: 640px) {
          .client-offer-panel { flex: none !important; width: 100% !important; }
          .client-signup-wrap { flex-direction: column !important; }
        }
      `}</style>
    </div>
  );
}
